import React, { useMemo } from 'react';
import { Pressable, type TextStyle, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import type { TypographyToken } from '@/theme/typography';

export type FieldTriggerProps = {
  label?: string;
  required?: boolean;
  uppercaseLabel?: boolean;
  labelVariant?: TypographyToken;
  labelGap?: number;
  labelStyle?: TextStyle | TextStyle[];
  placeholder?: string;
  /** Value text; `null`/empty shows the placeholder in the muted color. */
  displayValue?: string | null;
  disabled?: boolean;
  status?: 'default' | 'error';
  errorMessage?: string;
  triggerVariant?: TypographyToken;
  triggerStyle?: ViewStyle | ViewStyle[];
  disableStyle?: ViewStyle | ViewStyle[];
  onPress: () => void;
};

/**
 * A read-only field "trigger" that mirrors the look of `FormSelectInput` (label row, bordered
 * box with a value/placeholder + chevron, error text) but opens a custom sheet instead of the
 * built-in Select. Shared by the Country/Region and State/Province pickers so they line up with
 * the surrounding form fields.
 */
export function FieldTrigger({
  label,
  required = false,
  uppercaseLabel = false,
  labelVariant = 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
  labelGap = 4,
  labelStyle,
  placeholder,
  displayValue,
  disabled = false,
  status = 'default',
  errorMessage,
  triggerVariant = 'Font Guardian App/Subtitle/Subtitle 14 Regular',
  triggerStyle,
  disableStyle,
  onPress,
}: FieldTriggerProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const displayLabel = useMemo(() => {
    const text = String(label ?? '');
    return uppercaseLabel ? text.toUpperCase() : text;
  }, [label, uppercaseLabel]);

  const isPlaceholder = !displayValue;
  const chevronColor = disabled ? colors['5'] : colors.Blue;

  return (
    <Stack direction="column" gap={labelGap} width="100%">
      {label ? (
        <Stack direction="row" align="center" justify="flex-start">
          <Text variant={labelVariant} style={[styles.label, labelStyle]}>
            {displayLabel}
          </Text>
          {required ? (
            <Text variant={labelVariant} style={styles.requiredMark}>
              *
            </Text>
          ) : null}
        </Stack>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={onPress}
        style={[
          styles.trigger,
          triggerStyle,
          status === 'error' ? styles.triggerError : undefined,
          disabled ? disableStyle : undefined,
        ]}>
        <Text
          variant={triggerVariant}
          numberOfLines={1}
          style={[styles.value, isPlaceholder ? styles.placeholder : undefined]}>
          {isPlaceholder ? (placeholder ?? '') : displayValue}
        </Text>
        <Stack style={styles.chevron}>
          <SvgIcon name="IcPaginationArrow" size={scale(16)} color={chevronColor} />
        </Stack>
      </Pressable>

      {errorMessage ? (
        <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.errorText}>
          {errorMessage}
        </Text>
      ) : null}
    </Stack>
  );
}

export default FieldTrigger;

const useStyles = createStyles(({ scale }) => ({
  label: {
    color: colors['10'],
  },
  requiredMark: {
    marginLeft: 4,
    color: colors.Red,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: scale(8),
    // Match the FormInput box: the shared `fieldContent` style only carries the border color,
    // height and padding — the border width/radius/background come from the field primitive,
    // so we replicate them here (any overrides in `triggerStyle` still win).
    borderWidth: 1,
    borderColor: colors['3'],
    borderRadius: scale(8),
    minHeight: scale(48),
    paddingHorizontal: scale(12),
    backgroundColor: colors['0'],
  },
  triggerError: {
    borderColor: colors.Error,
  },
  value: {
    flex: 1,
    color: colors['10'],
  },
  placeholder: {
    color: colors['5'],
  },
  chevron: {
    transform: [{ rotate: '90deg' }],
  },
  errorText: {
    marginTop: -2,
    color: colors.Error,
  },
}));
